import { networkManager, WonderlandWebsocketEvent } from '@wonderlandcloud/client';
import { Events } from './network-events.js';
import { IntifaceRPCNode } from './IntifaceRPCNode.js';
import { ButtplugClientDevice, DeviceInfo } from 'buttplug';
import { DeviceRemoteCommand, DeviceRemoteStub, IntifaceController, SupportedAction, ALL_SUPPORTED_ACTIONS } from './IntifaceController.js';
import { EventsClient } from './events-client.js';
import { MenuController } from './menus/menu-controller.js';
import Logger from './utils/logger.js';
import { ConnectionError, ICEError, RPCError } from './utils/intiface-errors.js';

const MODULE = 'RemoteCon';

/**
 * IntifaceRemoteCon 
 * Controller side of an RPC relationship over RTCPeerConnection
 */
export class IntifaceRemoteCon extends IntifaceRPCNode {
    static TypeName = 'IntifaceRemoteCon';
    /** Timeout duration (in milliseconds) for establishing WebRTC connection with receiver */
    private static readonly CONNECTION_TIMEOUT_MS = 50000;
    /** Prevent aggressive repeated restartIce() calls from dual state handlers. */
    private static readonly ICE_RESTART_COOLDOWN_MS = 3000;

    /** Temporary negotiation key used to identify receiver during connection setup */
    private peerKey: string = "";
    /** Whether this controller is actively controlling a receiver */
    private controlling: boolean = false;
    /** Numeric peer ID assigned once WebRTC connection is established */
    private _peerID: number | undefined;
    /** Flag to prevent ICE restart attempts during intentional disconnection */
    private disconnecting: boolean = false;
    /** Timestamp of last ICE restart attempt to enforce cooldown. */
    private lastIceRestartAt: number = 0;
    /** Cache latest device sync received before numeric peer ID is assigned */
    private pendingDevicesJson: string | null = null;
    /** Keep websocket signaling handler alive for post-answer trickle ICE. */
    private onEventHandler: ((e: WonderlandWebsocketEvent) => void) | null = null;
    /**
     * Gets the numeric peer ID for this connection.
     * @throws {Error} If peer ID has not been assigned yet (connection not fully established)
     * @returns The peer's numeric ID
     */
    public get peerID(): number {
        if(this._peerID === undefined) {
            throw new Error("Peer ID is not set");
        }
        return this._peerID;
    }

    constructor() {
        super();
    }

    /**
     * Initiates WebRTC connection to a receiver using their temporary negotiation key.
     * Listens for ICE candidates and connection answers from the receiver.
     * @param peerKey - Temporary key provided by receiver to identify them during negotiation
     * @returns Promise resolving to true if connection successful, false if rejected
     * @throws {Error} If connection times out after CONNECTION_TIMEOUT_MS
     */
    async connectToPeer(peerKey: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            let timeoutHandle: number | null = null;

            const onEvent = (e: WonderlandWebsocketEvent) => {
                try {
                    switch (e.type) {
                        case Events.PLAYER_REQUESTING_DEVICECONTROL_ANSWER_ICE:
                            if (this._peerID !== undefined && e.data?.id !== this._peerID) {
                                // Ignore candidates from unrelated device-control sessions.
                                break;
                            }
                            Logger.debug(`Received ICE candidate from receiver: ${e.data.id}`, MODULE);

                            if (this.controlling) {
                                if (e.data && e.data.candidate) {
                                    if (this.intifacePC?.remoteDescription) {
                                        this.intifacePC?.addIceCandidate(e.data.candidate)
                                            .then(() => Logger.debug('Added ICE candidate', MODULE))
                                            .catch((err) => {
                                                const iceError = new ICEError('Failed to add ICE candidate', e.data.candidate);
                                                Logger.error('Could not add ICE candidate', MODULE, iceError);
                                            });
                                    }
                                    else {
                                        // Buffer candidate for later, but enforce max size to prevent memory bloat
                                        if (this.remoteCandidates.length < IntifaceRPCNode.MAX_REMOTE_CANDIDATES) {
                                            this.remoteCandidates.push(e.data.candidate);
                                        } else {
                                            Logger.warn(`ICE candidate buffer full (${IntifaceRPCNode.MAX_REMOTE_CANDIDATES}), discarding candidate. Connection may be failing.`, MODULE);
                                        }
                                    }
                                }
                            }
                            break;
                        case Events.PLAYER_REQUESTING_DEVICECONTROL_ANSWER:
                            Logger.info(`Received answer from receiver: ${e.data.id}`, MODULE);
                            if (this.intifacePC && this.intifacePC.localDescription) {
                                Logger.debug('Setting remote description', MODULE);
                                this.controlling = true;
                                this._peerID = e.data.id as number;
                                // Notify controller that this remote connection now has a stable numeric peerID.
                                try {
                                    IntifaceController.instance.onRemoteConnected(this.peerKey, this._peerID);
                                } catch (err) {
                                    Logger.error('Could not notify IntifaceController of remote connection', MODULE, err);
                                    // This is a critical error - reject the connection
                                    fullCleanup();
                                    reject(new ConnectionError('Failed to register remote connection', 'registration_failed'));
                                    return;
                                }
                                Logger.info(`Connected with peerID: ${this.peerID}`, MODULE);
                                this.flushPendingDeviceSync();
                                this.intifacePC.setRemoteDescription(e.data.description);
                            }
                            clearConnectionTimeout();
                            resolve(true);
                            break;
                        case Events.PLAYER_REQUESTING_DEVICECONTROL_REJECTION:
                            Logger.info(`Connection rejected: ${e.data.description}`, MODULE);
                            fullCleanup();
                            this.controlling = false;
                            this.peerKey = "";
                            resolve(false);
                            break;
                    }
                } catch (err) {
                    // Malformed event messages are logged but don't interrupt the connection flow
                    Logger.debug('Ignoring malformed network event during connection', MODULE, err);
                }
            }

            const clearConnectionTimeout = () => {
                if (timeoutHandle !== null) {
                    clearTimeout(timeoutHandle);
                    timeoutHandle = null;
                }
            };

            const removeEventHandler = () => {
                if (this.onEventHandler) {
                    networkManager.onEvent.remove(this.onEventHandler);
                    this.onEventHandler = null;
                }
            };

            const fullCleanup = () => {
                clearConnectionTimeout();
                removeEventHandler();
            };

            // Timeout
            timeoutHandle = window.setTimeout(() => {
                fullCleanup();
                reject(new ConnectionError(`Connection timeout after ${IntifaceRemoteCon.CONNECTION_TIMEOUT_MS}ms`, 'timeout'));
            }, IntifaceRemoteCon.CONNECTION_TIMEOUT_MS);

            let peerCon : RTCPeerConnection = this.createConPeerConnection();

            this.controlling = true;
            if (this.onEventHandler) {
                networkManager.onEvent.remove(this.onEventHandler);
            }
            this.onEventHandler = onEvent;
            networkManager.onEvent.add(onEvent);
            this.peerKey = peerKey;


            this.startNegotiation(peerKey, peerCon);
        });
    }

    /**
     * Starts WebRTC negotiation by creating data channel and registering RPC methods.
     * The data channel creation triggers the onnegotiationneeded event which sends the offer.
     * @param rec - Receiver's temporary negotiation key
     * @param peerCon - RTCPeerConnection instance to use
     */
    private async startNegotiation(rec: string, peerCon: RTCPeerConnection) {
        // create data channel
        this.intifaceDC = peerCon.createDataChannel("rtc-rpc-channel", { ordered: true });
        this.setupDataChannel(this.intifaceDC);
        this.onDataChannelReady.add(async () => {
            try {
                const snapshot = await this.call('requestDeviceSync');
                this.updateDevices(snapshot);
            } catch (err) {
                Logger.warn('Failed to request initial device sync from receiver', MODULE, err);
            }
        });
        // Register RPC methods for each connection to ensure proper binding to data channel
        this.registerMethod("updateDevices", this.updateDevices.bind(this));
        this.registerMethod("updateDeviceCommandState", this.updateDeviceCommandState.bind(this));
        this.registerMethod("stopDeviceCommand", this.stopDeviceCommand.bind(this));
    }

    /**
     * RPC method called by receiver to sync device list and states.
     * Preserves existing command states when updating device stubs.
     * @param devicesJson - JSON-serialized array of device stubs from receiver
     */
    private updateDevices(payload: string | { devicesJson?: string; peerId?: number }) {
        const devicesJson = typeof payload === 'string' ? payload : payload?.devicesJson;

        if (!devicesJson) {
            Logger.warn('Received empty device sync payload from receiver', MODULE);
            return;
        }

        const peerId = this._peerID;
        if (peerId === undefined) {
            // Device sync can arrive before the stable remote peer ID is assigned.
            this.pendingDevicesJson = devicesJson;
            Logger.warn('Received device sync before peer ID assignment; deferring update', MODULE);
            return;
        }

        Logger.debug('Updating devices from receiver', MODULE);
        let numMap : Map<number, DeviceRemoteStub> = IntifaceController.instance.devicifyJSON(devicesJson);
        // Merge lastCommand from previous map entries to preserve state across resyncs
        const existing = IntifaceController.instance.remoteDeviceMap.get(peerId);
        if (existing) {
            numMap.forEach((newStub, key) => {
                const oldStub = existing.get(key);
                if (!oldStub) return;
                
                // Preserve last command state for all action types
                ALL_SUPPORTED_ACTIONS.forEach((action: SupportedAction) => {
                    const lastCmd = oldStub.getLastCommand(action);
                    if (lastCmd) {
                        newStub.setLastCommand(action, lastCmd);
                    }
                });
            });
        }

        Logger.debug(`Updated ${numMap.size} devices for peer ${peerId}`, MODULE);
        IntifaceController.instance.remoteDeviceMap.set(peerId, numMap);
        
        MenuController.instance.updateDeviceControls();
    }

    /** Apply cached device sync that may arrive before peer ID assignment. */
    private flushPendingDeviceSync() {
        if (!this.pendingDevicesJson || this._peerID === undefined) {
            return;
        }

        const pending = this.pendingDevicesJson;
        this.pendingDevicesJson = null;
        this.updateDevices({ devicesJson: pending, peerId: this._peerID });
    }

    /**
     * RPC method called by receiver to notify controller of device command state changes.
     * Updates the local device stub with the authoritative command state from receiver.
     * @param payload - Object containing deviceIndex and command (or null to clear)
     */
    private updateDeviceCommandState(payload: any) {
        const deviceIndex: number = payload?.deviceIndex;
        const command: any = payload?.command;
        try {
            const remoteDevices = IntifaceController.instance.remoteDeviceMap.get(this.peerID);
            if (remoteDevices) {
                const stub = remoteDevices.get(deviceIndex);
                if (stub) {
                    if(command === null) {
                        stub.nullifyLastCommands();
                        MenuController.instance.updateDeviceControls();
                    } else {
                        // Persist last command state from receiver (includes dur and startMs)
                        stub.setLastCommand(command.actuateType, command);
                        MenuController.instance.updateDeviceControls();
                    }
                }
            }
        } catch (err) {
            Logger.error('Failed to update remote device command state', MODULE, err);
            // Re-throw to allow caller to handle the error
            throw new RPCError('Failed to update device command state', 'updateDeviceCommandState', payload);
        }
    }

    /**
     * Sends stop command to a remote device via RPC.
     * Clears local device stub state and calls receiver's stopDeviceCommand method.
     * @param remoteKey - Numeric peer ID of the receiver
     * @param deviceIndex - Index of the device to stop
     */
    public async sendStopRemoteDevice(remoteKey: number, deviceIndex: number) {
        Logger.debug(`Sending stop command to device ${deviceIndex}`, MODULE);
        const remoteDevices = IntifaceController.instance.remoteDeviceMap.get(remoteKey);
        if (remoteDevices) {
            const stub = remoteDevices.get(deviceIndex);
            if (stub) {
                stub.nullifyLastCommands();
            }
        }
        try {
            await this.call('stopDeviceCommand', { deviceIndex });
            MenuController.instance.updateDeviceControls();
        } catch (err) {
            Logger.error('Failed to call stopDeviceCommand on receiver', MODULE, err);
            throw new RPCError('Failed to stop remote device', 'stopDeviceCommand', { deviceIndex });
        }
    }

    /**
     * RPC method called by receiver to notify controller that a device was stopped.
     * Updates local device stub to clear all command states.
     * @param payload - Object containing deviceIndex
     */
    private stopDeviceCommand(payload: any) {
        const deviceIndex: number = (payload && payload.deviceIndex) ? payload.deviceIndex : undefined;
        if (deviceIndex === undefined) return;
        const remoteDevices = IntifaceController.instance.remoteDeviceMap.get(this.peerID);
        if (!remoteDevices) return;
        const stub = remoteDevices.get(deviceIndex);
        if (stub) {
            stub.nullifyLastCommands();
        }
        MenuController.instance.updateDeviceControls();
    }

    /**
     * Creates and configures the RTCPeerConnection for controller-to-receiver communication.
     * Sets up ICE handling, signaling state management, and connection lifecycle callbacks.
     * @returns Configured RTCPeerConnection instance
     */
    private createConPeerConnection(): RTCPeerConnection {
        const iceServers: RTCIceServer[] = [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:3478' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:3478' },
            { urls: 'stun:stun4.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:3478' },
        ];

        // Add TURN servers if available
        const turnServers = EventsClient.instance?.getTurnIceServers?.();
        if (turnServers?.length) {
            iceServers.push(...turnServers);
        }

        const intifacePC = new RTCPeerConnection({
            iceServers,
        });

        intifacePC.onicegatheringstatechange = (event) => {
            Logger.debug('ICE gathering state change',
                MODULE,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                event.target.iceGatheringState);
        };
        intifacePC.onicecandidate = (event) => {
            if (event.candidate) {
                const receiverId = this._peerID ?? this.peerKey;
                networkManager.client?.sendViaWs({
                    type: Events.PLAYER_REQUESTING_DEVICECONTROL_OFFER_ICE,
                    data: {
                        receiverId,
                        candidate: event.candidate,
                    },
                });
            }
        };
        intifacePC.onicecandidateerror = (event) => {
            Logger.warn('RemoteCon: ICE candidate error', MODULE, event);
        };
        intifacePC.onsignalingstatechange = () => {
            Logger.debug(`Signaling state: ${intifacePC.signalingState}`, MODULE);
            if (intifacePC &&
                (intifacePC.signalingState === 'have-remote-offer' ||
                    intifacePC.signalingState === 'stable')) {
                this.remoteCandidates.forEach((can) => {
                    if (intifacePC) {
                        intifacePC
                            .addIceCandidate(can)
                            .then(() => Logger.debug('Added cached ICE candidate', MODULE))
                            .catch((err) => {
                                const iceError = new ICEError('Failed to add cached ICE candidate', can);
                                Logger.error('Could not add cached ICE candidate', MODULE, iceError);
                            });
                    }
                });
                this.remoteCandidates = [];
            }
        };
        intifacePC.onnegotiationneeded = async (event) => {
            Logger.debug('Negotiation needed', MODULE);
            if (intifacePC.signalingState !== 'stable') {
                return;
            }
            const offer = await intifacePC.createOffer();
            Logger.debug('Created offer for receiver', MODULE);
                if (offer) {
                await intifacePC.setLocalDescription(offer);
                networkManager.client?.sendViaWs({
                    type: Events.PLAYER_REQUESTING_DEVICECONTROL_OFFER,
                    data: {
                        receiverId: this.peerKey,
                        data: offer,
                    }
                });
            }
        };
        intifacePC.oniceconnectionstatechange = () => {
            Logger.debug(`ICE connection state: ${intifacePC.iceConnectionState}`, MODULE);
            if (intifacePC.iceConnectionState === 'failed' && !this.disconnecting) {
                this.requestIceRestart(intifacePC, `ice:${intifacePC.iceConnectionState}`);
            }
        };
        // Cleanup/unregister when connection is closed or disconnected
        intifacePC.onconnectionstatechange = () => {
            Logger.debug(`Connection state: ${intifacePC.connectionState}`, MODULE);
            if (intifacePC.connectionState === 'failed') {
                this.requestIceRestart(intifacePC, `connection:${intifacePC.connectionState}`);
            }
            if (intifacePC.connectionState === 'failed') {
                Logger.error('Failed to establish RTCPeerConnection', MODULE);
                void this.logSelectedCandidatePair(intifacePC, 'controller');
            }

            if (intifacePC.connectionState === 'closed') {
                Logger.info('Connection closed', MODULE);
                this.disconnecting = true;

                if (this.onEventHandler) {
                    networkManager.onEvent.remove(this.onEventHandler);
                    this.onEventHandler = null;
                }
                
                // Attempt to unregister this remote from the controller - pass both peerKey and peerID
                try {
                    IntifaceController.instance.unregisterRemoteConnection(this.peerKey, this._peerID);
                } catch (err) {
                    // Log but don't re-throw - cleanup should continue even if unregister fails
                    Logger.error('Error unregistering remote peer', MODULE, err);
                }
                this.controlling = false;
                // Reset the disconnecting flag
                this.disconnecting = false;
                // Reset peerID to allow fresh connection
                this._peerID = undefined;
            }
        };
        this.intifacePC = intifacePC;
        
        return this.intifacePC;
    }

    /** Log selected ICE candidate pair details to diagnose connectivity failures. */
    private async logSelectedCandidatePair(pc: RTCPeerConnection, role: string): Promise<void> {
        try {
            const stats = await pc.getStats();
            let selectedPair: any = null;
            const candidatePairs: any[] = [];
            const candidates = new Map<string, any>();
            let selectedPairId: string | undefined;
            const localByType: Record<string, number> = {};
            const remoteByType: Record<string, number> = {};

            stats.forEach((report: any) => {
                if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
                    candidates.set(report.id, report);
                    const bucket = report.type === 'local-candidate' ? localByType : remoteByType;
                    const key = `${report.candidateType ?? 'unknown'}/${report.protocol ?? 'n/a'}`;
                    bucket[key] = (bucket[key] ?? 0) + 1;
                }
                if (report.type === 'candidate-pair') {
                    candidatePairs.push(report);
                    if (report.selected || report.nominated || report.state === 'succeeded') {
                        selectedPair = report;
                    }
                }
                if (report.type === 'transport' && report.selectedCandidatePairId) {
                    selectedPairId = report.selectedCandidatePairId;
                }
            });

            if (!selectedPair && selectedPairId) {
                selectedPair = candidatePairs.find((pair: any) => pair.id === selectedPairId) ?? null;
            }

            if (!selectedPair && candidatePairs.length > 0) {
                selectedPair = candidatePairs[0];
            }

            if (!selectedPair) {
                const localSummary = Object.entries(localByType).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
                const remoteSummary = Object.entries(remoteByType).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
                Logger.warn(
                    `No ICE candidate pair stats available for ${role}; local=[${localSummary}] remote=[${remoteSummary}]`,
                    MODULE
                );
                return;
            }

            const local = candidates.get(selectedPair.localCandidateId);
            const remote = candidates.get(selectedPair.remoteCandidateId);
            Logger.warn(
                `ICE pair (${role}) state=${selectedPair.state} nominated=${selectedPair.nominated} ` +
                `local=${local?.candidateType ?? 'unknown'}(${local?.protocol ?? 'n/a'}) ` +
                `remote=${remote?.candidateType ?? 'unknown'}(${remote?.protocol ?? 'n/a'})`,
                MODULE
            );
        } catch (err) {
            Logger.warn(`Failed to inspect ICE stats for ${role}`, MODULE, err);
        }
    }

    /** Attempt ICE restart with cooldown to avoid rapid restart loops on unstable networks. */
    private requestIceRestart(pc: RTCPeerConnection, reason: string): void {
        if (this.disconnecting) {
            return;
        }

        const now = Date.now();
        if (now - this.lastIceRestartAt < IntifaceRemoteCon.ICE_RESTART_COOLDOWN_MS) {
            Logger.warn(`Skipping ICE restart (${reason}) due to cooldown`, MODULE);
            return;
        }

        this.lastIceRestartAt = now;
        Logger.warn(`Attempting ICE restart (${reason})`, MODULE);
        pc.restartIce();
    }

    /**
     * Sends a device command to the receiver via RPC.
     * Optimistically updates local device stub before receiver confirms the change.
     * @param remoteKey - Numeric peer ID of the receiver
     * @param deviceIndex - Index of the device to control
     * @param actuateType - Type of actuation (vibrate, rotate, linear, etc.)
     * @param actuateVals - Array of actuation values (normalized by receiver)
     * @param duration - Optional duration in seconds
     * @param modVals - Optional modifier values (e.g., rotation direction)
     */
    public async sendRemoteDeviceCommand(remoteKey: number, deviceIndex: number, actuateType: SupportedAction, actuateVals: string[], duration?: string, modVals?: string[]) {
        Logger.debug(`Sending ${actuateType} command to device ${deviceIndex}`, MODULE);
        const cmd = IntifaceController.instance.createRemoteCommand(deviceIndex, actuateType, actuateVals, duration, modVals);
        // Update our local view of remote device's state before the receiver echoes back the authoritative state
        const remoteDevices = IntifaceController.instance.remoteDeviceMap.get(remoteKey);
        if (remoteDevices) {
            const stub = remoteDevices.get(deviceIndex) as DeviceRemoteStub;
            if (stub) {
                stub.setLastCommand(actuateType, cmd);
            }
        }
        this.call("sendDeviceCommand", cmd);
    }
}
